import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getTheme } from "@/lib/themes";
import { readableTextOn, shellOverlayIfNeeded, contrastRatio } from "@/lib/colorUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsContext = createContext(null);

/** Returns white / dark charcoal — whichever has higher WCAG contrast. */
function contrastText(hex) {
  return readableTextOn(hex);
}

/** Muted (65%) variant of the chosen text colour — via rgba alpha so
 *  the stop sits visually at ~65% opacity without changing the colour. */
function contrastMuted(hex) {
  const t = readableTextOn(hex);
  return t === "#FFFFFF" ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)";
}

/** Matching subtle border (10% opacity of the text colour). */
function contrastBorder(hex) {
  const t = readableTextOn(hex);
  return t === "#FFFFFF" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
}

/** Convert hex to HSL string for shadcn CSS vars (e.g., "222 20% 4%") */
function hexToHSL(hex) {
  if (!hex || hex.length < 7) return "0 0% 0%";
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({
    app_name: "DiNAXLP",
    primary_color: "#4A90D9",
    secondary_color: "#6B7280",
    learner_label: "Learner",
    help_email: "",
    admin_sidebar_color: "#0A0C10",
    admin_nav_color: "#0A0C10",
    admin_bg_color: "#0A0C10",
    admin_container_color: "#111318",
    user_sidebar_color: "#FFFFFF",
    user_nav_color: "#FFFFFF",
    user_bg_color: "#F8F9FC",
    user_container_color: "#FFFFFF",
  });

  const applyTheme = useCallback((s) => {
    const theme = getTheme(s?.theme_id);
    const primary = theme.primary;     // sidebar + top-nav bg
    const secondary = theme.secondary; // buttons, accents, active states
    const root = document.documentElement.style;

    // --- Accessibility: WCAG-accurate text/overlay resolution --------
    // Shell text: use the theme's explicit override if set, otherwise pick
    // white vs black by perceived brightness (YIQ threshold 0.36). If the
    // resulting contrast is still below AA, composite a subtle overlay to
    // push it above 4.5:1 without losing the brand hue.
    const shellText = theme.shellText || readableTextOn(primary);
    const shellOverlay = shellOverlayIfNeeded(primary, shellText) || "transparent";
    // On-secondary: button text. Explicit override > auto.
    const onSecondary = theme.onSecondary || readableTextOn(secondary);
    const inactiveOpacity = theme.shellTextInactiveOpacity ?? 0.65;
    const sectionLabelColor = theme.sectionLabelColor || secondary;
    const sectionLabelOpacity = theme.sectionLabelOpacity ?? 1.0;
    const activeBorderWidth = `${theme.activeBorderWidth ?? 3}px`;
    const activeIconColor = theme.activeIconColor || shellText;
    const topNavBreadcrumbWeight = String(theme.breadcrumbWeight ?? 500);
    const isWhiteText = shellText.toUpperCase() === "#FFFFFF";

    // --- Content-area icons (dashboards, cards, stat chips) -----------
    // Every light-content icon should render in a coloured pill: the icon
    // itself in one of the two theme hexes, the pill bg in the opposing
    // hex. `contentIconOn` in the theme catalog decides which side the
    // icon lands on. Default: icon=primary, bg=secondary.
    const iconOn = theme.contentIconOn || "primary";
    const contentIconColor = iconOn === "primary" ? primary : secondary;
    const contentIconBg = iconOn === "primary" ? secondary : primary;

    // --- Top-nav search "glass" effect (spec) --------------------------
    // When the shell uses white text (dark/saturated themes), the search
    // bar reads as a frosted-white overlay: bg rgba(255,255,255,0.12) +
    // border rgba(255,255,255,0.2). On light-shell themes (Monocle), the
    // polarity flips: bg rgba(0,0,0,0.05) + border rgba(0,0,0,0.1). The
    // placeholder pulls the shell text at 60% opacity.
    const topNavSearchBg = isWhiteText ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.05)";
    const topNavSearchBorder = isWhiteText ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)";
    const topNavSearchPlaceholder = isWhiteText ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)";

    // Per-theme rgba helpers built from the (potentially explicit) shell
    // text colour so muted rgbas pair correctly on Vitality / other
    // overrides.
    const mutedRgba = isWhiteText ? `rgba(255,255,255,${inactiveOpacity})` : `rgba(0,0,0,${inactiveOpacity})`;
    const borderRgba = isWhiteText ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";

    root.setProperty("--shell-text", shellText);
    root.setProperty("--shell-text-muted", mutedRgba);
    root.setProperty("--shell-text-muted-opacity", String(inactiveOpacity));
    root.setProperty("--shell-section-label", sectionLabelColor);
    root.setProperty("--shell-section-label-opacity", String(sectionLabelOpacity));
    root.setProperty("--shell-overlay", shellOverlay);
    root.setProperty("--shell-active-border-width", activeBorderWidth);
    root.setProperty("--shell-active-icon-color", activeIconColor);
    root.setProperty("--on-secondary", onSecondary);
    root.setProperty("--topnav-search-bg", topNavSearchBg);
    root.setProperty("--topnav-search-border", topNavSearchBorder);
    root.setProperty("--topnav-search-placeholder", topNavSearchPlaceholder);
    root.setProperty("--topnav-breadcrumb-weight", topNavBreadcrumbWeight);
    root.setProperty("--content-icon-color", contentIconColor);
    root.setProperty("--content-icon-bg", contentIconBg);
    root.setProperty("--theme-primary", primary);
    root.setProperty("--theme-secondary", secondary);

    // Legacy vars — kept so existing components (buttons, pills, active
    // outlines) that reference them don't break. `--primary-color` is
    // historically the brand accent (= theme secondary). For SVG icons that
    // need the deeper brand color, use `--theme-primary` directly instead.
    root.setProperty("--primary-color", secondary);
    root.setProperty("--secondary-color", primary);

    // Admin — theme paints sidebar + top nav; body stays a soft neutral.
    const aSidebar = primary;
    const aNav = primary;
    const aBg = "#ebeef5";
    const aContainer = "#f0fcff";
    root.setProperty("--admin-sidebar-bg", aSidebar);
    root.setProperty("--admin-nav-bg", aNav);
    root.setProperty("--admin-bg", aBg);
    root.setProperty("--admin-container-bg", aContainer);
    root.setProperty("--admin-container-text", contrastText(aContainer));
    root.setProperty("--admin-container-muted", contrastMuted(aContainer));
    root.setProperty("--admin-container-border", contrastBorder(aContainer));
    root.setProperty("--admin-sidebar-text", shellText);
    root.setProperty("--admin-sidebar-muted", shellText === "#FFFFFF" ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)");
    root.setProperty("--admin-sidebar-border", contrastBorder(aSidebar));
    root.setProperty("--admin-nav-text", shellText);
    root.setProperty("--admin-nav-muted", shellText === "#FFFFFF" ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)");
    root.setProperty("--admin-nav-border", contrastBorder(aNav));
    root.setProperty("--admin-text", contrastText(aBg));
    root.setProperty("--admin-muted", contrastMuted(aBg));
    root.setProperty("--admin-border", contrastBorder(aBg));

    // Learner portal — sidebar + top nav pick up the theme primary; body
    // stays light per product rule.
    const uSidebar = primary;
    const uNav = primary;
    const uBg = "#F8F9FC";
    const uContainer = "#FFFFFF";
    root.setProperty("--user-sidebar-bg", uSidebar);
    root.setProperty("--user-nav-bg", uNav);
    root.setProperty("--user-bg", uBg);
    root.setProperty("--user-container-bg", uContainer);
    root.setProperty("--user-container-text", contrastText(uContainer));
    root.setProperty("--user-container-muted", contrastMuted(uContainer));
    root.setProperty("--user-container-border", contrastBorder(uContainer));
    root.setProperty("--user-sidebar-text", shellText);
    root.setProperty("--user-sidebar-muted", shellText === "#FFFFFF" ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)");
    root.setProperty("--user-sidebar-border", contrastBorder(uSidebar));
    root.setProperty("--user-nav-text", shellText);
    root.setProperty("--user-nav-muted", shellText === "#FFFFFF" ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)");
    root.setProperty("--user-nav-border", contrastBorder(uNav));
    root.setProperty("--user-text", contrastText(uBg));
    root.setProperty("--user-muted", contrastMuted(uBg));
    root.setProperty("--user-border", contrastBorder(uBg));

    // Shadcn root tokens
    root.setProperty("--background", hexToHSL(aBg));
    root.setProperty("--foreground", hexToHSL(contrastText(aBg)));
    root.setProperty("--popover", hexToHSL(aContainer));
    root.setProperty("--popover-foreground", hexToHSL(contrastText(aContainer)));
    root.setProperty("--card", hexToHSL(aContainer));
    root.setProperty("--card-foreground", hexToHSL(contrastText(aContainer)));
    root.setProperty("--muted", hexToHSL(aContainer));
    root.setProperty("--muted-foreground", hexToHSL(contrastMuted(aContainer)));
    root.setProperty("--border", hexToHSL(contrastBorder(aBg) === "rgba(0,0,0,0.1)" ? "#DDDDDD" : "#2A2D34"));
    root.setProperty("--input", hexToHSL(contrastBorder(aBg) === "rgba(0,0,0,0.1)" ? "#DDDDDD" : "#2A2D34"));

    document.body.style.backgroundColor = aBg;
    document.body.style.color = contrastText(aBg);

    // Diagnostic log — helpful when tweaking palettes. Only fires when
    // the detected contrast < AA, so console noise stays minimal.
    const ratio = contrastRatio(shellText, primary);
    if (ratio < 4.5) {
      console.info(
        `[theme] ${theme.name}: shell contrast ${ratio.toFixed(2)}:1 below AA — applying ${shellOverlay} overlay.`
      );
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`, { withCredentials: true });
      setSettings(data);
      applyTheme(data);
    } catch (e) {
      console.error("Failed to fetch settings:", e);
      applyTheme(null);
    }
  }, [applyTheme]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return (
    <SettingsContext.Provider value={{ settings, fetchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) throw new Error("useSettings must be used within SettingsProvider");
  return context;
}
